// ---------------------------------------------------------------------------
// Creator copy for a failed .fig import (UX audit, 2026-07-27)
//
// Same Record<LocaleCode, …> convention as generation-orphan-copy.ts. Every
// entry answers the three questions the audit found missing at the moment of
// failure:
//   title      — what failed, in the creator's words
//   meaning    — what it means for their work (what was and was NOT changed)
//   nextAction — ONE honest next step
// `retryable` says whether repeating the same action can plausibly succeed;
// it is used to decide whether a retry affordance is offered at all, so the
// UI never invites an action that cannot work.
//
// Rule (non-negotiable): no entry may contain a raw upstream body, an HTTP
// status, or a service-internal identifier — the reason enum in
// openpencil-import-failure.ts is the only bridge from transport to copy.
// ---------------------------------------------------------------------------

import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import { LOCALE_CODES } from "@/pages/cogo-marketing/i18n/locales";
import type { OpenPencilImportFailureReason } from "./openpencil-import-failure";

export interface OpenPencilImportFailureCopy {
  title: string;
  meaning: string;
  nextAction: string;
  retryable: boolean;
}

export const OPENPENCIL_IMPORT_FAILURE_COPY_LOCALES: readonly LocaleCode[] =
  LOCALE_CODES;

type ReasonCopy = Record<
  OpenPencilImportFailureReason,
  OpenPencilImportFailureCopy
>;

const EN: ReasonCopy = {
  "unsupported-file-type": {
    title: "This file type can't be read",
    meaning:
      "Coogo reads .fig and .pen design files. Nothing was uploaded and no design source was added.",
    nextAction: "Pick a .fig file exported from Figma.",
    retryable: false,
  },
  "empty-source-file": {
    title: "This file is empty",
    meaning:
      "The file you picked has no content, so there is nothing to read. Nothing was uploaded.",
    nextAction: "Export the file from Figma again, then pick the new .fig.",
    retryable: false,
  },
  "signed-out": {
    title: "You're signed out",
    meaning:
      "Reading a .fig source needs a signed-in account, so the import never started.",
    nextAction: "Sign in, then start the import again.",
    retryable: false,
  },
  "not-permitted": {
    title: "This source belongs to another account",
    meaning:
      "The signed-in account isn't allowed to read this source, so nothing was imported.",
    nextAction: "Switch accounts, or import your own copy of the .fig file.",
    retryable: false,
  },
  "service-not-configured": {
    title: "Source reading isn't available here",
    meaning:
      "Coogo's .fig reader isn't switched on for this site yet, so the design could not be read.",
    nextAction: "Try again later — this needs a Coogo fix, not a change to your file.",
    retryable: false,
  },
  "service-unavailable": {
    title: "Coogo couldn't read the file right now",
    meaning:
      "The .fig reader didn't answer. Your file wasn't changed and no design source was added.",
    nextAction: "Try the import again in a few minutes.",
    retryable: true,
  },
  "service-busy": {
    title: "Too many imports at once",
    meaning:
      "The .fig reader is busy, so this import wasn't accepted. Nothing was changed.",
    nextAction: "Wait a moment, then start the import again.",
    retryable: true,
  },
  "timed-out": {
    title: "Reading the file took too long",
    meaning:
      "The .fig reader didn't finish in time, so the design was not read.",
    nextAction: "Try the import again — large files often need a second attempt.",
    retryable: true,
  },
  "source-too-large": {
    title: "This file is too large to upload",
    meaning:
      "The .fig file is bigger than the upload limit, so it was never sent.",
    nextAction:
      "Remove unused pages in Figma, export again, then import the smaller file.",
    retryable: false,
  },
  "source-not-found": {
    title: "That source is no longer available",
    meaning:
      "The import Coogo was reading no longer exists, so nothing was saved.",
    nextAction: "Start the import again with your .fig file.",
    retryable: true,
  },
  "source-upload-failed": {
    title: "Your file didn't finish uploading",
    meaning:
      "The import was created, but the file's contents never reached Coogo, so there was nothing to read.",
    nextAction: "Retry the import; if it keeps failing, check your connection.",
    retryable: true,
  },
  "source-rejected": {
    title: "Coogo couldn't accept this file",
    meaning:
      "The .fig reader turned the file away before reading it, so nothing was added.",
    nextAction: "Export the file from Figma again, then retry the import.",
    retryable: true,
  },
  "import-conflict": {
    title: "This import already moved on",
    meaning:
      "The import is in a state that can't take this step, so nothing changed.",
    nextAction: "Start a fresh import with your .fig file.",
    retryable: true,
  },
  "stale-studio-build": {
    title: "Studio needs a reload",
    meaning:
      "This tab is running an older version of Studio that can't reach the .fig reader.",
    nextAction: "Reload the page, then start the import again.",
    retryable: false,
  },
  cancelled: {
    title: "Import stopped",
    meaning:
      "The import stopped before it finished, so nothing was saved.",
    nextAction: "Start the import again when you're ready.",
    retryable: true,
  },
  unknown: {
    title: "This file could not be read",
    meaning:
      "Coogo couldn't read the design from this file. Nothing was added to your design sources.",
    nextAction: "Try the import again, or pick a different .fig file.",
    retryable: true,
  },
};

const KO: ReasonCopy = {
  "unsupported-file-type": {
    title: "읽을 수 없는 파일 형식이에요",
    meaning:
      "Coogo는 .fig와 .pen 디자인 파일을 읽어요. 업로드된 것도, 추가된 디자인 소스도 없어요.",
    nextAction: "Figma에서 내보낸 .fig 파일을 선택해 주세요.",
    retryable: false,
  },
  "empty-source-file": {
    title: "파일이 비어 있어요",
    meaning:
      "선택한 파일에 내용이 없어서 읽을 것이 없어요. 업로드되지 않았어요.",
    nextAction: "Figma에서 다시 내보낸 뒤 새 .fig 파일을 선택해 주세요.",
    retryable: false,
  },
  "signed-out": {
    title: "로그인되어 있지 않아요",
    meaning:
      ".fig 소스를 읽으려면 로그인이 필요해서 가져오기가 시작되지 않았어요.",
    nextAction: "로그인한 뒤 다시 가져오기를 시작해 주세요.",
    retryable: false,
  },
  "not-permitted": {
    title: "다른 계정의 소스예요",
    meaning:
      "지금 로그인한 계정은 이 소스를 읽을 권한이 없어서 아무것도 가져오지 않았어요.",
    nextAction: "계정을 바꾸거나 본인의 .fig 파일로 가져와 주세요.",
    retryable: false,
  },
  "service-not-configured": {
    title: "여기서는 소스 읽기를 사용할 수 없어요",
    meaning:
      "이 사이트에는 Coogo의 .fig 리더가 아직 켜져 있지 않아 소스 사실을 만들 수 없었어요.",
    nextAction: "파일 문제가 아니라 Coogo 설정 문제예요. 잠시 후 다시 시도해 주세요.",
    retryable: false,
  },
  "service-unavailable": {
    title: "지금은 파일을 읽지 못했어요",
    meaning:
      ".fig 리더가 응답하지 않았어요. 파일은 그대로이고 디자인 소스도 추가되지 않았어요.",
    nextAction: "몇 분 뒤에 다시 가져오기를 시도해 주세요.",
    retryable: true,
  },
  "service-busy": {
    title: "동시에 너무 많은 가져오기가 진행 중이에요",
    meaning:
      ".fig 리더가 바빠서 이번 요청은 받아들여지지 않았어요. 변경된 것은 없어요.",
    nextAction: "잠시 기다렸다가 다시 가져오기를 시작해 주세요.",
    retryable: true,
  },
  "timed-out": {
    title: "파일 읽기가 너무 오래 걸렸어요",
    meaning: ".fig 리더가 시간 안에 끝내지 못해 소스 사실을 만들지 못했어요.",
    nextAction: "다시 시도해 주세요. 큰 파일은 두 번째 시도에서 되는 경우가 많아요.",
    retryable: true,
  },
  "source-too-large": {
    title: "업로드하기에 파일이 너무 커요",
    meaning: ".fig 파일이 업로드 한도를 넘어서 전송되지 않았어요.",
    nextAction:
      "Figma에서 사용하지 않는 페이지를 정리해 다시 내보낸 뒤 가져와 주세요.",
    retryable: false,
  },
  "source-not-found": {
    title: "해당 소스를 더 이상 찾을 수 없어요",
    meaning: "Coogo가 읽던 가져오기가 더 이상 존재하지 않아 저장된 것이 없어요.",
    nextAction: ".fig 파일로 가져오기를 다시 시작해 주세요.",
    retryable: true,
  },
  "source-upload-failed": {
    title: "파일 업로드가 끝나지 않았어요",
    meaning:
      "가져오기는 만들어졌지만 파일 내용이 Coogo에 도착하지 않아 읽을 것이 없었어요.",
    nextAction: "가져오기를 다시 시도해 주세요. 계속 실패하면 연결 상태를 확인해 주세요.",
    retryable: true,
  },
  "source-rejected": {
    title: "이 파일을 받아들이지 못했어요",
    meaning: ".fig 리더가 파일을 읽기 전에 거절해서 추가된 것이 없어요.",
    nextAction: "Figma에서 다시 내보낸 뒤 가져오기를 다시 시도해 주세요.",
    retryable: true,
  },
  "import-conflict": {
    title: "이미 다음 단계로 넘어간 가져오기예요",
    meaning: "이 가져오기는 지금 이 단계를 받을 수 없는 상태라 변경된 것이 없어요.",
    nextAction: ".fig 파일로 새로 가져오기를 시작해 주세요.",
    retryable: true,
  },
  "stale-studio-build": {
    title: "Studio를 새로고침해야 해요",
    meaning:
      "이 탭은 .fig 리더에 연결할 수 없는 예전 버전의 Studio를 실행하고 있어요.",
    nextAction: "페이지를 새로고침한 뒤 다시 가져오기를 시작해 주세요.",
    retryable: false,
  },
  cancelled: {
    title: "가져오기가 중단됐어요",
    meaning: "가져오기가 끝나기 전에 멈춰서 저장된 소스 사실이 없어요.",
    nextAction: "준비되면 다시 가져오기를 시작해 주세요.",
    retryable: true,
  },
  unknown: {
    title: "이 파일을 읽지 못했어요",
    meaning:
      "이 파일에서 소스 사실을 만들지 못했어요. 디자인 소스에 추가된 것은 없어요.",
    nextAction: "다시 시도하거나 다른 .fig 파일을 선택해 주세요.",
    retryable: true,
  },
};

const JA: ReasonCopy = {
  "unsupported-file-type": {
    title: "この形式のファイルは読み取れません",
    meaning:
      "Coogo が読み取れるのは .fig と .pen のデザインファイルです。アップロードもデザインソースの追加も行われていません。",
    nextAction: "Figma から書き出した .fig ファイルを選んでください。",
    retryable: false,
  },
  "empty-source-file": {
    title: "ファイルが空です",
    meaning:
      "選ばれたファイルに中身がないため読み取れません。アップロードは行われていません。",
    nextAction: "Figma から書き出し直し、新しい .fig を選んでください。",
    retryable: false,
  },
  "signed-out": {
    title: "サインインしていません",
    meaning:
      ".fig ソースの読み取りにはサインインが必要なため、取り込みは開始されませんでした。",
    nextAction: "サインインしてから取り込みをやり直してください。",
    retryable: false,
  },
  "not-permitted": {
    title: "別のアカウントのソースです",
    meaning:
      "現在のアカウントにはこのソースを読む権限がないため、何も取り込まれていません。",
    nextAction: "アカウントを切り替えるか、自分の .fig を取り込んでください。",
    retryable: false,
  },
  "service-not-configured": {
    title: "ここではソース読み取りを利用できません",
    meaning:
      "このサイトでは Coogo の .fig リーダーがまだ有効になっておらず、ソース事実を作成できませんでした。",
    nextAction: "ファイル側の問題ではありません。時間をおいて再度お試しください。",
    retryable: false,
  },
  "service-unavailable": {
    title: "今はファイルを読み取れませんでした",
    meaning:
      ".fig リーダーから応答がありませんでした。ファイルは変更されず、デザインソースも追加されていません。",
    nextAction: "数分後に取り込みをやり直してください。",
    retryable: true,
  },
  "service-busy": {
    title: "取り込みが混み合っています",
    meaning:
      ".fig リーダーが混雑しているため受け付けられませんでした。変更はありません。",
    nextAction: "少し待ってから取り込みをやり直してください。",
    retryable: true,
  },
  "timed-out": {
    title: "読み取りに時間がかかりすぎました",
    meaning:
      ".fig リーダーが時間内に終わらず、ソース事実を作成できませんでした。",
    nextAction: "もう一度お試しください。大きなファイルでは再試行で成功することがあります。",
    retryable: true,
  },
  "source-too-large": {
    title: "アップロードするには大きすぎます",
    meaning: ".fig ファイルが上限を超えているため送信されませんでした。",
    nextAction:
      "Figma で不要なページを整理して書き出し直し、小さくしてから取り込んでください。",
    retryable: false,
  },
  "source-not-found": {
    title: "そのソースは見つかりません",
    meaning: "読み取っていた取り込みが存在しないため、保存されたものはありません。",
    nextAction: ".fig ファイルで取り込みをやり直してください。",
    retryable: true,
  },
  "source-upload-failed": {
    title: "ファイルのアップロードが完了しませんでした",
    meaning:
      "取り込みは作成されましたが、ファイルの内容が Coogo に届かなかったため、読み取る対象がありませんでした。",
    nextAction: "取り込みをやり直してください。繰り返し失敗する場合は接続を確認してください。",
    retryable: true,
  },
  "source-rejected": {
    title: "このファイルを受け付けられませんでした",
    meaning:
      ".fig リーダーが読み取る前に受け付けを拒否したため、追加はありません。",
    nextAction: "Figma から書き出し直して取り込みをやり直してください。",
    retryable: true,
  },
  "import-conflict": {
    title: "この取り込みはすでに次の段階に進んでいます",
    meaning: "この操作を受け付けられない状態のため、変更はありません。",
    nextAction: ".fig ファイルで新しく取り込みを開始してください。",
    retryable: true,
  },
  "stale-studio-build": {
    title: "Studio の再読み込みが必要です",
    meaning:
      "このタブは .fig リーダーに接続できない古いバージョンの Studio を実行しています。",
    nextAction: "ページを再読み込みしてから取り込みをやり直してください。",
    retryable: false,
  },
  cancelled: {
    title: "取り込みを中止しました",
    meaning: "完了前に中止されたため、ソース事実は保存されていません。",
    nextAction: "準備ができたら取り込みをやり直してください。",
    retryable: true,
  },
  unknown: {
    title: "このファイルを読み取れませんでした",
    meaning:
      "このファイルからソース事実を作成できませんでした。デザインソースには何も追加されていません。",
    nextAction: "もう一度試すか、別の .fig ファイルを選んでください。",
    retryable: true,
  },
};

const ZH: ReasonCopy = {
  "unsupported-file-type": {
    title: "无法读取这种文件类型",
    meaning:
      "Coogo 可读取 .fig 与 .pen 设计文件。本次没有上传任何文件，也没有新增设计来源。",
    nextAction: "请选择从 Figma 导出的 .fig 文件。",
    retryable: false,
  },
  "empty-source-file": {
    title: "文件是空的",
    meaning: "所选文件没有内容，无法读取，也没有上传。",
    nextAction: "请在 Figma 中重新导出，再选择新的 .fig 文件。",
    retryable: false,
  },
  "signed-out": {
    title: "尚未登录",
    meaning: "读取 .fig 源需要登录账号，本次导入没有开始。",
    nextAction: "登录后重新开始导入。",
    retryable: false,
  },
  "not-permitted": {
    title: "该来源属于其他账号",
    meaning: "当前登录的账号无权读取该来源，未导入任何内容。",
    nextAction: "请切换账号，或导入你自己的 .fig 文件。",
    retryable: false,
  },
  "service-not-configured": {
    title: "此处暂不支持读取源文件",
    meaning: "本站尚未启用 Coogo 的 .fig 读取服务，因此无法生成源事实。",
    nextAction: "这不是文件的问题，请稍后再试。",
    retryable: false,
  },
  "service-unavailable": {
    title: "现在无法读取该文件",
    meaning: ".fig 读取服务没有响应。你的文件未被更改，也没有新增设计来源。",
    nextAction: "几分钟后再试一次导入。",
    retryable: true,
  },
  "service-busy": {
    title: "同时导入的请求过多",
    meaning: ".fig 读取服务正忙，本次请求未被受理，没有任何更改。",
    nextAction: "稍等片刻后重新开始导入。",
    retryable: true,
  },
  "timed-out": {
    title: "读取文件耗时过长",
    meaning: ".fig 读取服务未能按时完成，没有生成源事实。",
    nextAction: "请重试一次；较大的文件通常需要第二次尝试。",
    retryable: true,
  },
  "source-too-large": {
    title: "文件过大，无法上传",
    meaning: ".fig 文件超出上传上限，并未发送。",
    nextAction: "在 Figma 中删除未使用的页面后重新导出，再导入较小的文件。",
    retryable: false,
  },
  "source-not-found": {
    title: "该来源已不存在",
    meaning: "Coogo 正在读取的导入已不存在，没有保存任何源事实。",
    nextAction: "请用 .fig 文件重新开始导入。",
    retryable: true,
  },
  "source-upload-failed": {
    title: "文件未上传完成",
    meaning: "导入已创建，但文件内容没有到达 Coogo，因此没有可读取的内容。",
    nextAction: "请重试导入；若持续失败，请检查网络连接。",
    retryable: true,
  },
  "source-rejected": {
    title: "无法接受该文件",
    meaning: ".fig 读取服务在读取前就拒绝了该文件，未新增任何内容。",
    nextAction: "请在 Figma 中重新导出后再试。",
    retryable: true,
  },
  "import-conflict": {
    title: "该导入已进入下一阶段",
    meaning: "当前状态无法接受这一步操作，没有任何更改。",
    nextAction: "请用 .fig 文件重新开始一次导入。",
    retryable: true,
  },
  "stale-studio-build": {
    title: "需要重新加载 Studio",
    meaning: "此标签页运行的是旧版 Studio，无法连接 .fig 读取服务。",
    nextAction: "请重新加载页面后再开始导入。",
    retryable: false,
  },
  cancelled: {
    title: "导入已停止",
    meaning: "导入在完成前停止，没有保存源事实。",
    nextAction: "准备好后重新开始导入。",
    retryable: true,
  },
  unknown: {
    title: "无法读取该文件",
    meaning: "Coogo 未能从该文件生成源事实，设计来源中没有新增任何内容。",
    nextAction: "请重试导入，或换一个 .fig 文件。",
    retryable: true,
  },
};

const ZH_TW: ReasonCopy = {
  "unsupported-file-type": {
    title: "無法讀取這種檔案類型",
    meaning:
      "Coogo 可讀取 .fig 與 .pen 設計檔。本次沒有上傳任何檔案，也沒有新增設計來源。",
    nextAction: "請選擇從 Figma 匯出的 .fig 檔案。",
    retryable: false,
  },
  "empty-source-file": {
    title: "檔案是空的",
    meaning: "所選檔案沒有內容，無法讀取，也沒有上傳。",
    nextAction: "請在 Figma 重新匯出，再選擇新的 .fig 檔案。",
    retryable: false,
  },
  "signed-out": {
    title: "尚未登入",
    meaning: "讀取 .fig 原始檔需要登入帳號，本次匯入並未開始。",
    nextAction: "登入後重新開始匯入。",
    retryable: false,
  },
  "not-permitted": {
    title: "此來源屬於其他帳號",
    meaning: "目前登入的帳號沒有權限讀取此來源，未匯入任何內容。",
    nextAction: "請切換帳號，或匯入你自己的 .fig 檔案。",
    retryable: false,
  },
  "service-not-configured": {
    title: "此處暫時無法讀取原始檔",
    meaning: "本站尚未啟用 Coogo 的 .fig 讀取服務，因此無法產生來源事實。",
    nextAction: "這不是檔案的問題，請稍後再試。",
    retryable: false,
  },
  "service-unavailable": {
    title: "現在無法讀取這個檔案",
    meaning: ".fig 讀取服務沒有回應。你的檔案未被更動，也沒有新增設計來源。",
    nextAction: "幾分鐘後再試一次匯入。",
    retryable: true,
  },
  "service-busy": {
    title: "同時進行的匯入太多",
    meaning: ".fig 讀取服務忙碌，本次請求未被受理，沒有任何變更。",
    nextAction: "稍待片刻後重新開始匯入。",
    retryable: true,
  },
  "timed-out": {
    title: "讀取檔案花費太久",
    meaning: ".fig 讀取服務未能及時完成，沒有產生來源事實。",
    nextAction: "請重試一次；較大的檔案通常需要第二次嘗試。",
    retryable: true,
  },
  "source-too-large": {
    title: "檔案太大，無法上傳",
    meaning: ".fig 檔案超過上傳上限，並未送出。",
    nextAction: "在 Figma 移除未使用的頁面後重新匯出，再匯入較小的檔案。",
    retryable: false,
  },
  "source-not-found": {
    title: "該來源已不存在",
    meaning: "Coogo 正在讀取的匯入已不存在，沒有保存任何來源事實。",
    nextAction: "請用 .fig 檔案重新開始匯入。",
    retryable: true,
  },
  "source-upload-failed": {
    title: "檔案未上傳完成",
    meaning: "匯入已建立，但檔案內容沒有送達 Coogo，因此沒有可讀取的內容。",
    nextAction: "請重試匯入；若持續失敗，請檢查網路連線。",
    retryable: true,
  },
  "source-rejected": {
    title: "無法接受這個檔案",
    meaning: ".fig 讀取服務在讀取前就拒絕了此檔案，沒有新增任何內容。",
    nextAction: "請在 Figma 重新匯出後再試一次。",
    retryable: true,
  },
  "import-conflict": {
    title: "這次匯入已進入下一階段",
    meaning: "目前狀態無法接受這個步驟，沒有任何變更。",
    nextAction: "請用 .fig 檔案重新開始一次匯入。",
    retryable: true,
  },
  "stale-studio-build": {
    title: "需要重新載入 Studio",
    meaning: "此分頁執行的是舊版 Studio，無法連線到 .fig 讀取服務。",
    nextAction: "請重新載入頁面後再開始匯入。",
    retryable: false,
  },
  cancelled: {
    title: "匯入已停止",
    meaning: "匯入在完成前停止，沒有保存來源事實。",
    nextAction: "準備好後重新開始匯入。",
    retryable: true,
  },
  unknown: {
    title: "無法讀取這個檔案",
    meaning: "Coogo 無法從這個檔案產生來源事實，設計來源中沒有新增任何內容。",
    nextAction: "請重試匯入，或改用其他 .fig 檔案。",
    retryable: true,
  },
};

const COPY: Record<LocaleCode, ReasonCopy> = {
  en: EN,
  ko: KO,
  ja: JA,
  zh: ZH,
  "zh-TW": ZH_TW,
};

export function getOpenPencilImportFailureCopy(
  locale: LocaleCode,
  reason: OpenPencilImportFailureReason,
): OpenPencilImportFailureCopy {
  const table = COPY[locale] ?? COPY.en;
  return table[reason] ?? table.unknown;
}
